<template>
  <div id="top" class="provider-workspace-page">
    <LoadingDiv v-if="!isReady" />
    <template v-else>
      <div class="provider-workspace-intro">
        <UiPageHeader :title="capWords(provider?.name || 'Provider')" description="Manage this provider account, tariff records, and access settings.">
          <template #actions><router-link :to="{ name: 'provider' }" class="provider-workspace-back"><i class="material-icons" aria-hidden="true">arrow_back</i>All providers</router-link></template>
        </UiPageHeader>
        <div class="provider-workspace-context">
          <span class="context-pill" :class="providerStatusClass">{{ providerStatusLabel }}</span>
          <span class="context-pill">{{ provider?.state || 'State not recorded' }}</span>
          <span class="provider-workspace-contact">{{ provider?.phone || 'No phone recorded' }}</span>
          <span class="context-pill">{{ provider?.opid ? 'oneProvider synced' : 'Sync pending' }}</span>
        </div>
      </div>
      <div class="provider-workspace-layout">
        <nav class="provider-workspace-sidebar" aria-label="Provider sections">
          <div v-for="tab in tabsMenu" :key="tab.id" class="provider-workspace-nav-group">
            <button type="button" class="provider-workspace-nav-item" :class="{ 'is-active': currTabMenu?.id === tab.id }"
              :aria-expanded="tab.title === 'Tariff' ? currTabMenu?.id === tab.id : undefined"
              :aria-controls="tab.title === 'Tariff' ? 'provider-tariff-links' : 'provider-account-workspace'"
              :aria-current="currTabMenu?.id === tab.id && tab.title !== 'Tariff' ? 'page' : undefined" @click="handleActiveMenu(tab)">
              <i class="material-icons" aria-hidden="true">{{ tab.title === 'Profile' ? 'business' : tab.title === 'Tariff' ? 'list_alt' : 'settings' }}</i>
              <span>{{ tab.title }}</span>
              <i v-if="tab.title === 'Tariff'" class="material-icons provider-workspace-chevron" aria-hidden="true">{{ currTabMenu?.id === tab.id ? 'expand_less' : 'expand_more' }}</i>
            </button>
            <div id="provider-tariff-links" class="provider-workspace-subnav" v-if="tab.title === 'Tariff' && currTabMenu?.id === tab.id">
              <button v-for="child in tariffTabs" :key="child.id" type="button" class="provider-workspace-subnav-item"
                :class="{ 'is-active': currTabSmall === child.title }" :aria-current="currTabSmall === child.title ? 'page' : undefined"
                aria-controls="provider-account-workspace" @click="handleActiveSmall(child.title)">{{ child.title.split(' -> ')[1] }}</button>
            </div>
          </div>
          <router-link :to="{ name: 'providerSync' }" class="provider-workspace-nav-item"><i class="material-icons" aria-hidden="true">sync</i><span>Provider sync</span></router-link>
        </nav>
        <div id="provider-account-workspace" class="provider-workspace-content" :aria-label="currTabSmall || 'Provider workspace'">
          <div class="provider-workspace-section-heading" v-if="currTabMenu?.title === 'Tariff'">
            <h2>{{ currTabSmall?.split(' -> ')[1] }}</h2><p>{{ subModeDescription }}</p>
          </div>
        <section
          class="provider-secondary-body-frame"
          :class="{ 'is-tariff-section': currTabSmall?.includes('Tariff') }"
        >
          <LoadingDiv v-if="!currTabSmall" />

          <!-- Profile -->
          <ProfileDiv :provider="provider" @update="handleUpdate" v-if="currTabSmall?.includes('Profile')" />
          <!-- Tariff View -->
          <TariffDiv :provider="provider" startTab="view" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('View')" />
          <!-- Tariff Add -->
          <TariffDiv :provider="provider" startTab="add" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Add')" />
          <!-- Tariff Replace -->
          <TariffDiv :provider="provider" startTab="replace" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Replace')" />
          <!-- Tariff Band -->
          <TariffDiv :provider="provider" startTab="band" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('Band')" />
          <!-- Tariff AI Marker -->
          <TariffDiv :provider="provider" startTab="ai" @reload="handleReload"
            v-if="currTabSmall?.includes('Tariff') && currTabSmall?.includes('AI Marker')" />
          <!-- Settings -->
          <SettingsDiv :easyHMOProvider="provider" @reload="handleReload" v-if="currTabSmall?.includes('Settings')" />
        </section>
        </div>
      </div>
    </template>
  </div>
</template>



<script>
import { computed, onMounted, onUpdated, ref, watchEffect } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import ProfileDiv from "@/components/provider/ProfileProviderComponent.vue";
import TariffDiv from "@/components/provider/TariffProviderComponent.vue";
import SettingsDiv from "@/components/provider/SettingsProviderComponent.vue";
import {
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import nativeUi from "@/ui/native-ui";


export default {
  name: "providerView",
  components: { UiPageHeader, LoadingDiv, ProfileDiv, SettingsDiv, TariffDiv },
  props: ["id"],
  setup(props) {
    onMounted(() => {
      // page name
      document.title = "View Single Provider";
    });

    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currTabMenu = ref(null);
    const tabsMenu = ref([]);
    const currTabSmall = ref(null);
    const tabsSmall = ref([]);
    const providerId = ref(props.id);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    const { routeToTab } = useModuleNavigation();

    // dropdown
    onMounted(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      nativeUi.Dropdown.init(elems, {});
    });

    onUpdated(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      nativeUi.Dropdown.init(elems, {});
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: "sync", url: "providerSync", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("View"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // UI modification
    const maxRule = (text) => {
      let maxLength = 15;
      if (text.length > maxLength) {
        return ` ${text.substring(0, maxLength)} ... `;
      } else {
        return ` ${text} `;
      }
    };

    onMounted(() => {
      // load tab
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Profile -> Review Profile" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> View All" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Add Item" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Replace All" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> Tariff Band" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Tariff -> AI Marker" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer" });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Settings -> Manage Settings" });
    });

    // Initiate
    onMounted(() => {
      // load tab
      // tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Menu", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Profile", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Tariff", dropdown: true });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Settings", dropdown: false });
      // set default
      currTabMenu.value = tabsMenu.value[0];
    });

    // handle curr status
    const handleActiveSmall = (tab) => {
      // update show and components
      currTabSmall.value = tab;
      // highlight menu
      if (currTabSmall.value) {
        currTabMenu.value = tabsMenu.value.find(x => currTabSmall.value.toLowerCase().includes(x.title.toLowerCase()));
      } else {
        currTabMenu.value = tabsMenu.value[0];
      }
    };

    // handle curr status
    const handleActiveMenu = (tab) => {

      // check non-drop-downs
      const newActive = tabsSmall.value.find(x => x.title === tab.title) ||
        tabsSmall.value.find(x => x.title.toLowerCase().includes(tab.title.toLowerCase()));
      if (newActive) {
        handleActiveSmall(newActive?.title);
      }else{
        handleActiveSmall();
      }

      // check non-drop-downs
      // if (!tab?.dropdown) {
      //   let newActive = tabsSmall.value.find(x => x.title.toLowerCase().includes(tab.title.toLowerCase()));
      //   if (newActive) {
      //     handleActiveSmall(newActive?.title);
      //   } else {
      //     handleActiveSmall();
      //   }
      // } else {
      //   handleActiveSmall();
      // }
    };

    // Title
    watchEffect(() => {
      if (response.value) {
        // load title
        setTimeout(() => {
          var elems = document.querySelectorAll(".tooltipped");
          nativeUi.Tooltip.init(elems, {});
        }, 2000);
      }
    });

    // get provider
    const getProvider = () => {
      (async () => {
        const { res, err } = await getItem("provider", props.id);

        if (res.value) {
          response.value = res.value.data;
          setDefaultSmallTab();
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    }

    // Initiate
    onMounted(() => {
      // start
      getProvider();
    });

    // handle emitted update
    const handleUpdate = (val) => {
      response.value = val;
    };

    // handle reload
    const handleReload = (val) => {
      if (val) {
        // restart
        getProvider();
      }
    };

    // output
    const provider = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const tariffTabs = computed(() => {
      return tabsSmall.value.filter(x => x.title.includes("Tariff"));
    });

    const setDefaultSmallTab = () => {
      if (currTabSmall.value) return;

      handleActiveSmall(provider.value?.status === "s" ? "Profile -> Review Profile" : "Tariff -> View All");
    };

    const subModeTitle = computed(() => {
      if (!currTabSmall.value?.includes("->")) {
        return "Tariff";
      }

      return currTabSmall.value.replace(" -> ", " ");
    });

    const subModeDescription = computed(() => {
      const descriptions = {
        "Tariff -> View All": "Review provider tariff items and current prices.",
        "Tariff -> Add Item": "Add a new tariff item for this provider.",
        "Tariff -> Replace All": "Replace this provider's tariff records in bulk.",
        "Tariff -> Tariff Band": "Manage tariff band rules for this provider.",
        "Tariff -> AI Marker": "Mark provider tariff items that can support auto-vetting.",
      };

      return descriptions[currTabSmall.value] || "Select the tariff task.";
    });

    const providerStatusLabel = computed(() => {
      if (provider.value?.status === "s") {
        return "Inactive provider";
      }

      if (provider.value?.status === "p") {
        return "Pending approval";
      }

      return "Active provider";
    });

    const providerStatusClass = computed(() => {
      if (provider.value?.status === "s") {
        return "is-inactive";
      }

      if (provider.value?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      handleActiveSmall,
      handleActiveMenu,
      handleReload,
      handleUpdate,
      capWords,
      maxRule,
      isReady,
      tabsSmall,
      currTabMenu,
      tabsMenu,
      currTabSmall,
      error,
      providerId,
      provider,
      providerStatusClass,
      providerStatusLabel,
      tariffTabs,
      subModeTitle,
      subModeDescription,
    };
  },
};
</script>

<style scoped>
.provider-workspace-page {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}
.provider-workspace-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2.5rem; padding-bottom: 1.75rem; }
.provider-workspace-intro :deep(.ui-page-header) { margin-bottom: 0; }
.provider-workspace-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.provider-workspace-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.provider-workspace-context { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; margin-top: 1rem; }
.provider-workspace-contact { color: var(--ui-muted-foreground); font-size: .8125rem; overflow-wrap: anywhere; }
.provider-workspace-back { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); display: inline-flex; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.5rem; padding: 0 .9rem; text-decoration: none; white-space: nowrap; }
.provider-workspace-back i { font-size: 1rem; }
.provider-workspace-layout { align-items: start; display: grid; gap: 3.5rem; grid-template-columns: 15rem minmax(0, 1fr); }
.provider-workspace-sidebar { display: flex; flex-direction: column; gap: .25rem; }
.provider-workspace-nav-item { align-items: center; background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); cursor: pointer; display: grid; font-family: var(--ui-font-sans); font-size: .875rem; font-weight: 500; gap: .75rem; grid-template-columns: 1.25rem minmax(0, 1fr) 1rem; min-height: 2.75rem; padding: .6rem .75rem; text-align: left; width: 100%; }
.provider-workspace-nav-item i { font-size: 1.125rem; }
.provider-workspace-nav-item:is(:hover, .is-active) { background: var(--ui-muted); }
.provider-workspace-nav-item.is-active { font-weight: 600; }
.provider-workspace-chevron { color: var(--ui-muted-foreground); }
.provider-workspace-subnav { display: flex; flex-direction: column; gap: .125rem; margin: .375rem 0 .75rem 1.35rem; padding-left: .65rem; }
.provider-workspace-subnav-item { align-items: center; background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 3px); color: var(--ui-muted-foreground); cursor: pointer; display: flex; font-family: var(--ui-font-sans); font-size: .8125rem; gap: .5rem; justify-content: space-between; min-height: 2.25rem; padding: .4rem .65rem; text-align: left; width: 100%; }
.provider-workspace-subnav-item:is(:hover, .is-active) { background: var(--ui-muted); color: var(--ui-foreground); }
.provider-workspace-subnav-item.is-active { font-weight: 600; }
.provider-workspace-subnav-item small { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: 999px; color: var(--ui-muted-foreground); font-size: .6875rem; min-width: 1.5rem; padding: .1rem .35rem; text-align: center; }
.provider-workspace-page :is(button, a):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
.provider-workspace-content { min-width: 0; }
.provider-workspace-section-heading { border-bottom: 1px solid var(--ui-border); margin-bottom: 1.25rem; padding-bottom: 1rem; }
.provider-workspace-section-heading h2 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; margin: 0 0 .35rem; }
.provider-workspace-section-heading p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.5; margin: 0; }
.provider-workspace-nav-item { text-decoration: none; }
@layer components {
  .provider-workspace-page .provider-secondary-body-frame { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
  .provider-workspace-page :deep(a), .provider-workspace-page :deep(a *), .provider-workspace-page :deep(button), .provider-workspace-page :deep(.material-icons) { text-decoration: none !important; }
}
@media (max-width: 1100px) {
  .provider-workspace-page { padding: 1.5rem 1.5rem 2.5rem; }
  .provider-workspace-layout { gap: 2rem; grid-template-columns: 13rem minmax(0, 1fr); }
}
@media (max-width: 900px) {
  .provider-workspace-layout { gap: 1.5rem; grid-template-columns: minmax(0, 1fr); }
  .provider-workspace-sidebar { border-bottom: 1px solid var(--ui-border); display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); padding-bottom: 1rem; }
}
@media (max-width: 600px) {
  .provider-workspace-page { padding: 1.25rem 1rem 2rem; }
  .provider-workspace-intro { margin-bottom: 1.5rem; }
}

.provider-secondary-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.provider-workbench {
  color: #263238;
  padding-bottom: 48px;
  padding-top: 0;
  position: relative;
  width: 100%;
  z-index: 1;
}

.provider-super-card,
.provider-dossier-card,
.provider-mode-strip,
.provider-submode-strip,
.provider-secondary-body-frame,
.provider-workbench :deep(.card),
.provider-workbench :deep(.card-panel),
.provider-workbench :deep(.provider-tariff-card),
.provider-workbench :deep(.provider-tariff-search-card),
.provider-workbench :deep(.provider-tariff-row),
.provider-workbench :deep(.provider-tariff-empty),
.provider-workbench :deep(.profile-card),
.provider-workbench :deep(.settings-summary),
.provider-workbench :deep(.setting-card) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.provider-secondary-header {
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.provider-super-card {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 10px 20px -24px rgba(121, 85, 72, 0.55);
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.provider-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.provider-dossier-card {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  padding: 26px 34px;
  position: relative;
}

.provider-dossier-title {
  min-width: 0;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
  text-align: left;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.provider-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: none;
}

.provider-dossier-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.provider-dossier-title h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.provider-dossier-title p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.provider-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.context-pill.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.context-pill.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.context-pill.is-sync {
  background: #eef5fb;
  border-color: #d7e6f1;
  color: #285a82;
}

.provider-mode-strip,
.provider-submode-strip {
  align-items: center;
  border: 1px solid #c4d3d9;
  border-top: 0;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.provider-mode-strip {
  background: #fbfcfd;
  border-radius: 0 0 6px 6px;
  box-shadow: none;
  color: #263238;
  min-height: 76px;
  padding: 16px 20px;
}

.provider-mode-strip.has-submode {
  border-radius: 0;
}

.provider-submode-strip {
  background: #ffffff;
  border-top: 1px solid #e1e8eb;
  border-radius: 0 0 6px 6px;
  min-height: 78px;
  padding: 16px 20px;
}

.provider-submode-copy {
  display: grid;
  gap: 3px;
}

.provider-submode-copy span {
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.provider-submode-copy small {
  color: #607d8b;
  font-weight: 700;
}

.provider-home-icon-action {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #90a4ae;
  border-radius: 50%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
  width: 34px;
}

.provider-home-icon-action:hover {
  background: #e0f2f1;
  border-color: #00897b;
  box-shadow: 0 8px 18px -16px rgba(0, 105, 92, 0.72);
  color: #00695c;
  transform: translateY(-1px);
}

.provider-home-icon-action i {
  font-size: 18px;
  line-height: 18px;
}

.provider-mode-actions,
.provider-submode-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.provider-mode-actions {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(183, 197, 202, 0.72);
  border-radius: 6px;
  gap: 4px;
  padding: 5px;
}

.provider-mode-option,
.provider-submode-option {
  border-radius: 6px;
  cursor: pointer;
  font-weight: 800;
  min-height: 38px;
  padding: 0 15px;
}

.provider-mode-option {
  background: transparent;
  border: 1px solid transparent;
  color: #263238;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.provider-mode-option:hover {
  background: #eef3f6;
  border-color: #d7e1e5;
}

.provider-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 6px 14px -10px rgba(38, 50, 56, 0.75);
  color: #ffffff;
}

.provider-submode-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  color: #455a64;
  display: inline-flex;
  font-size: 0.8rem;
  gap: 8px;
  letter-spacing: 0;
  min-height: 42px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.provider-submode-option:hover {
  background: #eef7f7;
  border-color: #80cbc4;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.55);
  color: #00695c;
}

.provider-submode-option.is-active {
  border-color: #00897b;
  color: #00695c;
}

.provider-secondary-body-frame {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.provider-secondary-body-frame.is-tariff-section {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  margin-top: 14px;
  padding: 16px;
}

.provider-secondary-body-frame :deep(.card),
.provider-secondary-body-frame :deep(.card-panel) {
  border-color: #c4d3d9 !important;
  border-radius: 6px;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.provider-secondary-body-frame :deep(.provider-tariff-card),
.provider-secondary-body-frame :deep(.provider-tariff-search-card),
.provider-secondary-body-frame :deep(.provider-tariff-row),
.provider-secondary-body-frame :deep(.provider-tariff-empty),
.provider-secondary-body-frame :deep(.profile-card),
.provider-secondary-body-frame :deep(.settings-summary),
.provider-secondary-body-frame :deep(.setting-card) {
  border-color: #c4d3d9 !important;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.provider-secondary-body-frame :deep(.provider-tariff-row:hover),
.provider-secondary-body-frame :deep(.profile-card:hover),
.provider-secondary-body-frame :deep(.setting-card:hover) {
  border-color: #80cbc4 !important;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62) !important;
}

.provider-secondary-body-frame :deep(.tariff-edit-action),
.provider-secondary-body-frame :deep(.provider-tariff-head),
.provider-secondary-body-frame :deep(.tariff-price),
.provider-secondary-body-frame :deep(.context-pill),
.provider-secondary-body-frame :deep(.provider-tariff-context span) {
  border-color: #d5e1e5 !important;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-dossier-context {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .provider-mode-actions,
  .provider-submode-actions {
    justify-content: flex-start;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  .provider-submode-strip .provider-submode-actions {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fit, minmax(126px, 1fr));
    overflow-x: visible;
    padding-bottom: 0;
  }

  .provider-submode-strip .provider-submode-option {
    justify-content: center;
    min-width: 0;
    width: 100%;
  }
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .provider-secondary-surface > .container {
    width: 92%;
  }

  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip {
    padding: 22px;
  }

  .provider-secondary-body-frame,
  .provider-secondary-body-frame.is-tariff-section {
    padding: 18px;
  }

  .provider-mode-actions {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }

  .provider-mode-option {
    flex: 0 0 auto;
  }
}

@media only screen and (max-width: 600px) {
  .provider-super-card {
    align-items: flex-start;
  }

  .provider-dossier-card,
  .provider-secondary-body-frame {
    padding: 20px;
  }

  .provider-dossier-title h5 {
    font-size: 1.45rem;
  }

  .provider-dossier-title p {
    font-size: 0.94rem;
  }

  .provider-dossier-context {
    gap: 6px;
  }

  .provider-mode-actions {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .provider-mode-actions::-webkit-scrollbar {
    display: none;
  }

  .provider-mode-option {
    flex: 0 0 auto;
  }

  .provider-secondary-body-frame,
  .provider-secondary-body-frame.is-tariff-section {
    padding: 12px;
  }

  .provider-secondary-body-frame :deep(.provider-tariff-card),
  .provider-secondary-body-frame :deep(.provider-tariff-search-card),
  .provider-secondary-body-frame :deep(.provider-tariff-row),
  .provider-secondary-body-frame :deep(.provider-tariff-empty),
  .provider-secondary-body-frame :deep(.profile-card),
  .provider-secondary-body-frame :deep(.settings-summary),
  .provider-secondary-body-frame :deep(.setting-card) {
    border-radius: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .provider-super-card,
  .provider-dossier-card,
  .provider-mode-strip,
  .provider-submode-strip,
  .provider-secondary-body-frame,
  .provider-workbench :deep(.card),
  .provider-workbench :deep(.card-panel),
  .provider-workbench :deep(.provider-tariff-card),
  .provider-workbench :deep(.provider-tariff-search-card),
  .provider-workbench :deep(.provider-tariff-row),
  .provider-workbench :deep(.provider-tariff-empty),
  .provider-workbench :deep(.profile-card),
  .provider-workbench :deep(.settings-summary),
  .provider-workbench :deep(.setting-card) {
    animation: none;
  }
}
</style>
